/**
 * SoleadLab 2.0 — Asset Node Store
 *
 * B3.5: Zustand store for asset_nodes read surface.
 *
 * Site-scoped store: setSiteId clears stale data on site switch.
 * getChildren(parentId) returns direct children — used by AssetTree
 * for recursive rendering without re-filtering the full array.
 */

import { create } from 'zustand';
import { fetchAssetNodes } from '@/services/assetNodeApi';
import type { AssetNode, AssetNodeFilters } from '@/services/assetNodeApi';

interface AssetNodeState {
  items: AssetNode[];
  loading: boolean;
  error: string | null;
  siteId: string | null;

  setSiteId: (siteId: string) => void;
  fetchItems: (filters?: AssetNodeFilters) => Promise<void>;
  getById: (id: string) => AssetNode | undefined;
  getChildren: (parentAssetId: string | null) => AssetNode[];
  clear: () => void;
}

export const useAssetNodeStore = create<AssetNodeState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  siteId: null,

  setSiteId(siteId) {
    if (siteId !== get().siteId) {
      set({ siteId, items: [], error: null });
    }
  },

  async fetchItems(filters?) {
    const { siteId } = get();
    if (!siteId) return;
    set({ loading: true, error: null });
    try {
      const { items } = await fetchAssetNodes(siteId, filters);
      set({ items, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load asset nodes.',
      });
    }
  },

  getById(id) {
    return get().items.find((n) => n.id === id);
  },

  getChildren(parentAssetId) {
    return get().items.filter((n) => n.parentAssetId === parentAssetId);
  },

  clear() {
    set({ items: [], error: null, siteId: null });
  },
}));
